import React, { Dispatch, useEffect } from 'react';
import { Button, Collapse, Table } from 'reactstrap';

import i18n, {
  DateFormatOptionsForFileNames,
  getDateFormatOptions,
} from '@/utils/i18n';
import ClassicLoadingSpinner from '../../../../components/LoadingSpinner/ClassicLoadingSpinner';
import {
  DepositClearingDataEntry,
  getInvoiceData,
  InvoiceDataInput,
} from '../../EventUtils/InvoiceDataCalculation';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import { getDepositClearingCsvBlob } from '../../EventUtils/DepositClearingReport';
import { download, sanitizeFilename } from '@/utils/download';
import { FaAngleDown, FaAngleRight, FaFileDownload } from 'react-icons/fa';
import DateTimePicker from '../../../../components/DateTimePicker';
import { ApolloError } from 'apollo-client';

type DepositClearingTableProps = {
  eventId: string;
  data: InvoiceDataInput;
  error: ApolloError | undefined;
  loading: boolean;
  refetch: () => Promise<unknown>;
  startDate: Date | null;
  setStartDate: Dispatch<React.SetStateAction<Date | null>>;
  endDate: Date | null;
  setEventEndDate: Dispatch<React.SetStateAction<Date | null>>;
};

export const DepositClearingTable = ({
  eventId,
  data,
  error,
  loading,
  refetch,
  startDate,
  setStartDate,
  endDate,
  setEventEndDate,
}: DepositClearingTableProps) => {
  const [transformedData, setTransformedData] = React.useState<
    Record<string, DepositClearingDataEntry[]>
  >({});
  const [expandedItemLocationIds, setExpandedItemLocationIds] = React.useState<
    string[]
  >([]);
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] =
    React.useState<boolean>(false);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<Date>(new Date());

  useEffect(() => {
    if (data) {
      setLastUpdatedAt(new Date());
      getInvoiceData(data).then((transformedData) => {
        setTransformedData(transformedData);
      });
    }
  }, [data]);

  function toggleExpandedItemLocation(id: string) {
    if (expandedItemLocationIds.includes(id)) {
      setExpandedItemLocationIds(
        expandedItemLocationIds.filter((i) => i !== id),
      );
    } else {
      setExpandedItemLocationIds([...expandedItemLocationIds, id]);
    }
  }

  async function downloadDepositClearing() {
    if (data == null) {
      LayoutStoreInstance.setError(i18n.t('DepositClearingTable:noData'));
      return;
    }

    const blob = await getDepositClearingCsvBlob(data);
    const date = new Date()
      .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
      .replaceAll('/', '-');
    const fileName = sanitizeFilename(`pfand_clearing_${eventId}_${date}.csv`);
    download(blob, fileName);
  }

  if (error || (data == null && !loading)) {
    console.log(error);
    return <div>{i18n.t('DepositClearingTable:errorLoading')}</div>;
  }

  return (
    <div>
      <Button
        className="float-end"
        onClick={() => setIsDateRangePickerOpen(!isDateRangePickerOpen)}
      >
        {isDateRangePickerOpen ? <FaAngleDown /> : <FaAngleRight />}
      </Button>

      <h3 className="mt-5 mb-3">{i18n.t('DepositClearingTable:filter')}</h3>

      <Collapse isOpen={isDateRangePickerOpen}>
        <p>{i18n.t('DepositClearingTable:filterExplanation')}</p>
        <h4>{i18n.t('Common:startDate')}</h4>
        <DateTimePicker dateTime={startDate} setDateTime={setStartDate} />
        <h4>{i18n.t('Common:endDate')}</h4>
        <DateTimePicker dateTime={endDate} setDateTime={setEventEndDate} />
      </Collapse>

      <h3 className="mt-5 mb-3">{i18n.t('DepositClearingTable:results')}</h3>

      <div className="d-flex justify-content-between">
        <p>
          {i18n.t('Common:lastUpdateAt', {
            lastUpdate: lastUpdatedAt.toLocaleDateString(
              i18n.language,
              getDateFormatOptions(true, i18n.language, false, true),
            ),
          })}
        </p>

        <div>
          <Button
            onClick={() => refetch().then(() => setLastUpdatedAt(new Date()))}
            className="ms-1 mb-2"
          >
            {i18n.t('Common:update')}
          </Button>
          <Button onClick={downloadDepositClearing} className="ms-1 mb-2">
            <FaFileDownload /> {i18n.t('Common:download')}
          </Button>
        </div>
      </div>

      <Table responsive>
        <thead>
          <tr>
            <th>{i18n.t('Common:name')}</th>
            <th>{i18n.t('DepositClearingTable:details')}</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={2}>
                {i18n.t('Common:loading') + ' '} <ClassicLoadingSpinner />{' '}
              </td>
            </tr>
          )}
          {!loading &&
            [...Object.keys(transformedData)]
              .sort((a, b) => a.localeCompare(b))
              .map((itemLocationName) => [
                <tr key={itemLocationName}>
                  <td>{itemLocationName}</td>
                  <td>
                    <Button
                      onClick={() =>
                        toggleExpandedItemLocation(itemLocationName)
                      }
                    >
                      {expandedItemLocationIds.includes(itemLocationName)
                        ? i18n.t('DepositClearingTable:hideDetails')
                        : i18n.t('DepositClearingTable:showDetails')}
                    </Button>
                  </td>
                </tr>,
                expandedItemLocationIds.includes(itemLocationName) && (
                  <tr key={`${itemLocationName}-details`}>
                    <td colSpan={2}>
                      <Table>
                        <thead>
                          <tr>
                            <th>
                              {i18n.t('DepositClearingTable:containerType')}
                            </th>
                            <th className="numbers-formatted">
                              {i18n.t('DepositClearingTable:delivered')}
                            </th>
                            <th className="numbers-formatted">
                              {i18n.t('DepositClearingTable:returnedDirty')}
                            </th>
                            <th className="numbers-formatted">
                              {i18n.t('DepositClearingTable:returnedClean')}
                            </th>
                            <th className="numbers-formatted">
                              {i18n.t('DepositClearingTable:tooManyReturned')}
                            </th>
                            <th className="numbers-formatted">
                              {i18n.t('DepositClearingTable:tooFewReturned')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {transformedData[itemLocationName].map((item) => (
                            <tr
                              key={`${itemLocationName}-details-${item.itemTypeCategory}-${item.itemTypeId}`}
                            >
                              <td>
                                {item.itemTypeName}
                                {/* <img
                              style={{ width: "50px", height: "50px" }}
                              alt={`container type id ${container.id} name ${container.name}`}
                              src={
                                item. || DEFAULT_CONTAINER_TYPE_ICON
                              }
                            ></img> */}
                              </td>
                              <td className="numbers-formatted">
                                {item.delivered}
                              </td>
                              <td className="numbers-formatted">
                                {item.returnDirty}
                              </td>
                              <td className="numbers-formatted">
                                {item.returnClean}
                              </td>
                              <td className="numbers-formatted">
                                {item.tooManyReturned}
                              </td>
                              <td className="numbers-formatted">
                                {item.tooFewReturned}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </td>
                  </tr>
                ),
              ])}
        </tbody>
      </Table>
    </div>
  );
};
